import React, {useEffect, useMemo, useState} from 'react'
import {KTCard, KTCardBody} from '../../../../_websoft/helpers'
import {useParams, useNavigate} from 'react-router-dom'
import {Table, Form, InputGroup, Button} from 'react-bootstrap'
import {getNaloziZaRolnu, obrisiStavkuSaPripremePaneli} from '../../../api/paneliPromet'
import {PripremaPrometaModel} from '../../../models/PripremaPrometaModel'
import {ToastContainer} from 'react-toastify'
import {prometRolnePaneli} from '../../../api/thermoPrometRolne'
import notifyToast from '../../../components/Toast/Toast'
import {getRolnaDetalji} from '../../../api/rolneDetalji'
import {TableActionButton} from '../../../components/TableActionBtn/TableActionButton'
import './PaneliPrometRolna.css'

const PaneliPrometRolna = () => {
    const {id} = useParams<{ id: string }>()
    const navigate = useNavigate()

    const [data, setData] = useState<PripremaPrometaModel[]>([])
    const [total, setTotal] = useState<number>(0)
    const [predvidjeniUtrosak, setPredvidjeniUtrosak] = useState<number>(0)
    const [tezinaRolne, setTezinaRolne] = useState<number>(0)
    const [brojRolne, setBrojRolne] = useState<number>(0)
    const [krajnjaKolicinaRolne, setKrajnjaKolicinaRolne] = useState<string>('')
    const [komentarNaToleranciju, setKomentarNaToleranciju] = useState('')

    const [rolnaDebljina, setRolnaDebljina] = useState('')
    const [rolnaSirina, setRolnaSirina] = useState('')
    const [rolnaBoja, setRolnaBoja] = useState('')
    const [rolnaProizvodjac, setRolnaProizvodjac] = useState('')

    const suggestedKrajnja = useMemo(() => Math.round(tezinaRolne - predvidjeniUtrosak), [tezinaRolne, predvidjeniUtrosak])
    const krajnjaTezina = Number(krajnjaKolicinaRolne)
    const ispravnaKrajnjaTezina = krajnjaKolicinaRolne.trim() !== '' && Number.isFinite(krajnjaTezina) && krajnjaTezina >= 0
    const ispravanPrijedlog = data.length > 0 && Number.isFinite(suggestedKrajnja)
    const donjaGranica = (suggestedKrajnja * 100 - Math.abs(suggestedKrajnja) * 2) / 100
    const gornjaGranica = (suggestedKrajnja * 100 + Math.abs(suggestedKrajnja) * 2) / 100
    const vanTolerancije = ispravnaKrajnjaTezina && ispravanPrijedlog &&
        (krajnjaTezina < donjaGranica || krajnjaTezina > gornjaGranica)
    const komentarObavezan = vanTolerancije && !komentarNaToleranciju.trim()

    useEffect(() => {
        const fetchData = async () => {
            try {
                const params = {rolnaId: id}
                const result = await getNaloziZaRolnu(params)

                setData(result.data)

                if (result.data?.length) {
                    setTezinaRolne(result.data[0].repromaterijal.ReprMatTrenutnaKolicina)
                    setBrojRolne(result.data[0].repromaterijal.ReprMatId)
                    calculateTotal(result.data)
                }

                const response = await getRolnaDetalji(Number(id))
                setRolnaDebljina(response.data.ReprMatDebljina ?? '')
                setRolnaSirina(response.data.ReprMatSirina ?? '')
                setRolnaBoja(response.data.boja_lica?.BojaNaziv ?? '')
                setRolnaProizvodjac(response.data.proizvodjac?.PartnerNaziv ?? '')
            } catch (error) {
                console.error('Error fetching data:', error)
                notifyToast(false, 'Greška pri učitavanju podataka.')
            }
        }

        fetchData()
    }, [id])

    const calculateTotal = (rows: any[], updateSuggestion = true) => {
        const t = rows.reduce((acc, item) => acc + parseFloat(item.KolicinaRolnePotrebna), 0)
        setTotal(t)
        if (updateSuggestion) setPredvidjeniUtrosak(t)
    }

    const handleKolicinaChange = (index: number, value: string) => {
        const newData = [...data]
        newData[index].KolicinaRolnePotrebna = value
        setData(newData)
        calculateTotal(newData)
    }

    const adjustQuantities = (rows: any[], krajnjaKolicina: number, tezina: number) => {
        const currentTotal = rows.reduce((acc, item) => acc + parseFloat(item.KolicinaRolnePotrebna), 0)
        const difference2 = tezina - krajnjaKolicina
        const difference = difference2 - currentTotal
        const adjustment = difference / rows.length

        return rows.map((item) => ({
            ...item,
            KolicinaRolnePotrebna: (parseFloat(item.KolicinaRolnePotrebna) + adjustment).toFixed(2),
        }))
    }

    const handleKrajnjaKolicinaChange = (e: any) => {
        const value = e.target.value
        setKrajnjaKolicinaRolne(value)

        if (value.trim() && Number.isFinite(Number(value)) && Number(value) >= 0 && data.length) {
            const adjustedData = adjustQuantities(data, parseFloat(value), tezinaRolne)
            setData(adjustedData)
            // Redistribution reflects the entered weight, but must not move its tolerance baseline.
            calculateTotal(adjustedData, false)
        }
    }

    const handleButtonClick = async () => {
        if (!ispravnaKrajnjaTezina || !ispravanPrijedlog) {
            notifyToast(false, 'Provjerite krajnju težinu rolne i prijedlog krajnje težine.')
            return
        }
        if (komentarObavezan) {
            notifyToast(false, 'Upisana krajnja kolicina je van tolerancije, molim da upisete komentar zasto je tako.')
            return
        }
        const additionalData = {
            brojRolne: id,
            krajnjaKolicina: krajnjaKolicinaRolne,
            VanTolerancije: vanTolerancije ? 1 : 0,
            KomentarNaToleranciju: vanTolerancije ? komentarNaToleranciju.trim() : null,
        }

        const payload = data.map((item) => ({
            brojNaloga: item.BrojNaloga,
            stavka: item.Stavka,
            gornjiLim: item.RolnaG,
            KolicinaRolnePotrebna: item.KolicinaRolnePotrebna,
            rolnaSkenirana: item.Skenirano,
            ...additionalData,
        }))

        const response: any = await prometRolnePaneli(payload)
        const responseData = response

        if (responseData.error === 0) {
            notifyToast(true, responseData.message)

            setTimeout(() => {
                navigate('/terminali/termopaneli/promet-rolne', {
                    replace: true,
                    state: {refresh: true},
                })
            }, 3000)
        } else {
            notifyToast(false, responseData.message || 'An unexpected error occurred.')
        }
    }

    const onDelete = (rowId: any) => {
        obrisiStavkuSaPripremePaneli(rowId)
            .then((response) => {
                notifyToast(true, response.data['message'])
                navigate('/terminali/termopaneli/promet-rolne', {state: {refresh: true}})
            })
            .catch((error) => {
                notifyToast(false, error?.data?.['message'] || 'Greška.')
            })
    }

    const onEdit = (rowId: any) => {
        notifyToast(false, 'Izmjena je van upotrebe u modulu!')
    }

    return (
        <KTCard className='paneli-promet-rolna'>
            <ToastContainer />
            <KTCardBody className='p-3 p-md-5'>
                <div className='d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4'>
                    <div className='promet-rolna-heading'>
                        <h1 className='fw-bolder mb-1 text-break'>Promet rolne: <span className='text-success'>{id}</span></h1>
                        <div className='text-muted'>Popuni krajnju težinu, pa kreiraj promet.</div>
                    </div>
                    <Button variant='outline-secondary' onClick={() => navigate(-1)}>Nazad</Button>
                </div>

                <section className='border rounded p-3 p-md-4 mb-4' aria-labelledby='detalji-rolne-heading'>
                    <h2 id='detalji-rolne-heading' className='fs-6 fw-semibold py-2 mb-0'>Detalji rolne · R-{brojRolne || id}</h2>
                    <dl className='row g-3 mt-1 mb-0'>
                        <div className='col-6 col-lg-4'>
                            <dt className='text-muted fw-normal'>Dimenzije</dt>
                            <dd className='fw-bold mb-0 text-break'>{rolnaDebljina || '-'} × {rolnaSirina || '-'}</dd>
                        </div>
                        <div className='col-6 col-lg-4'>
                            <dt className='text-muted fw-normal'>Boja</dt>
                            <dd className='fw-bold mb-0 text-break'>{rolnaBoja || '-'}</dd>
                        </div>
                        <div className='col-12 col-lg-4'>
                            <dt className='text-muted fw-normal'>Proizvođač</dt>
                            <dd className='fw-bold mb-0 text-break'>{rolnaProizvodjac || '-'}</dd>
                        </div>
                    </dl>
                </section>

                <div className='card border shadow-sm mb-4'>
                    <div className='card-body p-3 p-md-5'>
                        <div className='row g-3 mb-5'>
                            <div className='col-6'>
                                <div className='bg-light rounded p-3 p-md-4 h-100'>
                                    <div className='text-muted mb-1'>Trenutna težina</div>
                                    <div className='fw-bold fs-3 text-break'><span className='text-success'>{tezinaRolne || 0}</span> kg</div>
                                </div>
                            </div>
                            <div className='col-6'>
                                <div className='bg-light rounded p-3 p-md-4 h-100'>
                                    <div className='text-muted mb-1'>Prijedlog krajnje težine</div>
                                    <div className='fw-bold fs-3 text-break' data-testid='prijedlog-krajnje-tezine'>
                                        <span className='text-primary'>{Number.isFinite(suggestedKrajnja) ? suggestedKrajnja : 0}</span> kg
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='promet-rolna-form'>
                            <Form.Group controlId='krajnja-tezina-rolne' className='promet-rolna-weight'>
                                <Form.Label className='fw-bold'>Krajnja težina rolne</Form.Label>
                                <InputGroup size='lg'>
                                    <Form.Control
                                        type='number'
                                        inputMode='decimal'
                                        min={0}
                                        step='any'
                                        value={krajnjaKolicinaRolne}
                                        onChange={handleKrajnjaKolicinaChange}
                                        placeholder='npr. 150'
                                        className={vanTolerancije ? 'border-danger' : ''}
                                        aria-describedby={vanTolerancije ? 'upozorenje-tolerancija-rolne' : undefined}
                                    />
                                    <InputGroup.Text>kg</InputGroup.Text>
                                </InputGroup>
                                <div className='text-muted mt-2'>Ukupan utrošak: <span className='fw-semibold'>{Number.isFinite(total) ? total.toFixed(2) : '0.00'}</span> kg</div>
                            </Form.Group>
                            {vanTolerancije && (
                                <>
                                    <div id='upozorenje-tolerancija-rolne' className='promet-rolna-warning alert alert-danger text-danger fw-semibold mb-0' role='alert'>
                                        <i className='bi bi-exclamation-triangle-fill me-2' aria-hidden='true' />
                                        Upisana krajnja kolicina je van tolerancije, molim da upisete komentar zasto je tako.
                                    </div>
                                    <Form.Group controlId='komentar-tolerancija-rolne' className='promet-rolna-comment'>
                                        <Form.Label className='fw-semibold required'>Komentar na toleranciju</Form.Label>
                                        <Form.Control
                                            as='textarea'
                                            rows={4}
                                            value={komentarNaToleranciju}
                                            onChange={(e) => setKomentarNaToleranciju(e.target.value)}
                                            placeholder='Upišite razlog odstupanja krajnje težine'
                                            required
                                            aria-describedby='upozorenje-tolerancija-rolne'
                                        />
                                    </Form.Group>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <section className='card border shadow-sm'>
                    <div className='card-body p-3 p-md-5'>
                        <div className='d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3'>
                            <h2 className='fs-4 fw-bolder mb-0'>Rolna korištena za izradu naloga</h2>
                            <span className='text-muted'>Ukupno stavki: {data.length}</span>
                        </div>
                        <div className='promet-rolna-table-wrapper'>
                            <Table hover className='promet-rolna-table align-middle mb-0 table-row-bordered' aria-label='Stavke prometa rolne'>
                                <thead className='bg-primary text-white'>
                                    <tr>
                                        <th scope='col'>Broj naloga</th>
                                        <th scope='col'>Stavka</th>
                                        <th scope='col'>Gornji</th>
                                        <th scope='col'>Donji</th>
                                        <th scope='col'>Količina</th>
                                        <th scope='col'>Skenirano</th>
                                        <th scope='col'>Akcija</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.map((item, index) => (
                                        <tr key={item.Id} className='promet-rolna-item fw-semibold'>
                                            <td data-label='Broj naloga'>{item.BrojNaloga}</td>
                                            <td data-label='Stavka'>{item.Stavka}</td>
                                            <td data-label='Gornji'>
                                                <i className={item.RolnaG === 1 ? 'bi bi-check-circle-fill fs-4 text-success' : 'bi bi-x-circle-fill fs-4 text-danger'} role='img' aria-label={item.RolnaG === 1 ? 'Da' : 'Ne'} />
                                            </td>
                                            <td data-label='Donji'>
                                                <i className={item.RolnaG === 0 ? 'bi bi-check-circle-fill fs-4 text-success' : 'bi bi-x-circle-fill fs-4 text-danger'} role='img' aria-label={item.RolnaG === 0 ? 'Da' : 'Ne'} />
                                            </td>
                                            <td data-label='Količina (kg)' className='promet-rolna-quantity'>
                                                <Form.Control
                                                    type='number'
                                                    inputMode='decimal'
                                                    step='any'
                                                    aria-label={'Količina za nalog ' + item.BrojNaloga + ', stavka ' + item.Stavka}
                                                    value={item.KolicinaRolnePotrebna}
                                                    onChange={(e) => handleKolicinaChange(index, e.target.value)}
                                                />
                                            </td>
                                            <td data-label='Skenirano'>
                                                <span className={Number(item.Skenirano) === 1 ? 'badge bg-success px-3 py-2' : 'badge bg-danger px-3 py-2'}>
                                                    {Number(item.Skenirano) === 1 ? 'DA' : 'NE'}
                                                </span>
                                            </td>
                                            <td data-label='Akcija'>
                                                <TableActionButton onDelete={() => onDelete(item.Id)} onEdit={() => onEdit(item.Id)} />
                                            </td>
                                        </tr>
                                    ))}
                                    <tr className='promet-rolna-total bg-light fw-bold'>
                                        <td colSpan={4}>Ukupan utrošak:</td>
                                        <td>{Number.isFinite(total) ? total.toFixed(2) : '0.00'} kg</td>
                                        <td colSpan={2} className='promet-rolna-total-spacer' />
                                    </tr>
                                </tbody>
                            </Table>
                        </div>
                    </div>
                </section>

                <div className='card border shadow-sm mt-4'>
                    <div className='card-body p-3 p-md-5 promet-rolna-actions'>
                        <div className='text-muted'>Unesi krajnju težinu, zatim klikni Kreiraj promet rolne.</div>
                        <div>
                            <Button onClick={handleButtonClick} className='btn btn-success btn-lg w-100'
                                disabled={!ispravnaKrajnjaTezina || !ispravanPrijedlog || komentarObavezan}>
                                <i className='bi bi-check2-circle me-2' aria-hidden='true' />
                                Kreiraj promet rolne
                            </Button>
                            {!krajnjaKolicinaRolne && <div className='text-muted mt-2'>Obavezno: krajnja težina.</div>}
                            {komentarObavezan && <div className='text-danger mt-2'>Obavezno: komentar za odstupanje.</div>}
                        </div>
                    </div>
                </div>
            </KTCardBody>
        </KTCard>
    )
}

export default PaneliPrometRolna
